import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo } from 'react';
import clsx from 'clsx';
import { Box, CircularLoader, Typography } from 'seagull';
import { getTestIds } from 'seagull/utils/TestIds';
import { DARK_50, WCAG_AA_MIN, getContrastRatio } from '~/business-logic/contrastLogic';
import styles from './BrandPreview.module.scss';
const DARK_500 = '#9393b1';
export const GetBrandPreviewTID = (parentTestId)=>getTestIds({
        parentTestId
    });
function getContentColorForBackground(bgHex) {
    const contrastDark = getContrastRatio(bgHex, DARK_500);
    const contrastLight = getContrastRatio(bgHex, DARK_50);
    if (contrastLight >= WCAG_AA_MIN) return DARK_50;
    if (contrastDark >= WCAG_AA_MIN) return DARK_500;
    return DARK_500;
}
export const BrandPreview = (param)=>{
    let { testId, className, image, brandColor, primaryBgStart, primaryBgEnd, secondaryBgColor, loading } = param;
    const testIds = useMemo(()=>GetBrandPreviewTID(testId), [
        testId
    ]);
    const { BrandPreviewTestIds } = testIds;
    const isSolidPrimary = primaryBgStart === primaryBgEnd;
    const primaryBgCss = isSolidPrimary ? primaryBgStart : "linear-gradient(315deg, ".concat(primaryBgEnd, " 0%, ").concat(primaryBgStart, " 100%)");
    const sidebarContentColor = getContentColorForBackground(secondaryBgColor);
    const mainContentColor = getContentColorForBackground(primaryBgStart);
    return /*#__PURE__*/ _jsxs(Box, {
        className: clsx(styles.root, className),
        testId: BrandPreviewTestIds.Root(),
        style: loading ? {
            filter: 'blur(4px)'
        } : undefined,
        children: [
            /*#__PURE__*/ _jsxs(Box, {
                className: styles.card,
                style: {
                    background: secondaryBgColor
                },
                children: [
                    /*#__PURE__*/ _jsxs(Box, {
                        className: styles.leftPanel,
                        style: {
                            background: secondaryBgColor
                        },
                        testId: BrandPreviewTestIds.LeftPanel(),
                        children: [
                            /*#__PURE__*/ _jsx(Box, {
                                className: styles.logoSlot,
                                children: image ? /*#__PURE__*/ _jsx("img", {
                                    src: image,
                                    alt: "Logo",
                                    className: styles.logoImage,
                                    "data-testid": BrandPreviewTestIds.Logo()
                                }) : /*#__PURE__*/ _jsx(Box, {
                                    className: styles.logoEmpty,
                                    testId: BrandPreviewTestIds.LogoEmptySlot()
                                })
                            }),
                            /*#__PURE__*/ _jsxs(Box, {
                                className: styles.leftLines,
                                children: [
                                    /*#__PURE__*/ _jsx(Box, {
                                        className: styles.leftLineActive,
                                        style: {
                                            background: brandColor
                                        }
                                    }),
                                    /*#__PURE__*/ _jsx(Box, {
                                        className: styles.leftLineWide,
                                        style: {
                                            background: sidebarContentColor
                                        }
                                    }),
                                    /*#__PURE__*/ _jsx(Box, {
                                        className: styles.leftLineMedium,
                                        style: {
                                            background: sidebarContentColor
                                        }
                                    }),
                                    /*#__PURE__*/ _jsx(Box, {
                                        className: styles.leftLineShort,
                                        style: {
                                            background: sidebarContentColor
                                        }
                                    }),
                                    /*#__PURE__*/ _jsx(Box, {
                                        className: styles.leftLineWide,
                                        style: {
                                            background: sidebarContentColor
                                        }
                                    }),
                                    /*#__PURE__*/ _jsx(Box, {
                                        className: styles.leftLineMedium,
                                        style: {
                                            background: sidebarContentColor
                                        }
                                    }),
                                    /*#__PURE__*/ _jsx(Box, {
                                        className: styles.leftLineShort,
                                        style: {
                                            background: sidebarContentColor
                                        }
                                    })
                                ]
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs(Box, {
                        className: styles.rightPanel,
                        style: {
                            background: primaryBgCss
                        },
                        testId: BrandPreviewTestIds.RightPanel(),
                        children: [
                            /*#__PURE__*/ _jsxs(Box, {
                                className: styles.titleBlock,
                                children: [
                                    /*#__PURE__*/ _jsx(Box, {
                                        className: styles.titleLine,
                                        style: {
                                            background: mainContentColor
                                        }
                                    }),
                                    /*#__PURE__*/ _jsx(Box, {
                                        className: styles.subtitleLine,
                                        style: {
                                            background: mainContentColor
                                        }
                                    })
                                ]
                            }),
                            [
                                0,
                                1,
                                2
                            ].map((index)=>/*#__PURE__*/ _jsxs(Box, {
                                    className: styles.lineItem,
                                    style: {
                                        background: secondaryBgColor
                                    },
                                    testId: BrandPreviewTestIds.LineItem(index),
                                    children: [
                                        /*#__PURE__*/ _jsx(Box, {
                                            className: styles.lineItemTrack,
                                            style: {
                                                background: sidebarContentColor
                                            }
                                        }),
                                        /*#__PURE__*/ _jsx(Typography, {
                                            variant: "paragraph3",
                                            className: styles.dollarSign,
                                            style: {
                                                color: sidebarContentColor
                                            },
                                            children: "$"
                                        })
                                    ]
                                }, index)),
                            /*#__PURE__*/ _jsx(Box, {
                                className: styles.brandButton,
                                style: {
                                    background: brandColor
                                },
                                testId: BrandPreviewTestIds.BrandButton()
                            })
                        ]
                    })
                ]
            }),
            loading && /*#__PURE__*/ _jsx(Box, {
                className: styles.loadingOverlay,
                testId: BrandPreviewTestIds.LoadingOverlay(),
                children: /*#__PURE__*/ _jsx(CircularLoader, {})
            })
        ]
    });
};
